import { EDITOR_ACTIONS } from '~/constants/documentActions';
import { editableFields } from '~/constants/documentEditor';

import { encodeToBase64 } from '~/utils/message';

const getPath = (obj) => obj?.cssPath || obj?.id || '';

const getParentChildrenPairsFromLayout = (elements) => {
  if (!Array.isArray(elements) || elements.length === 0) {
    return { parent: null, children: [] };
  }

  const validElements = elements.filter((el) => getPath(el) !== '');

  // Find a parent that is prefix of at least one other element
  const parents = validElements.filter((p) =>
    validElements.some((c) => c !== p && getPath(c).startsWith(getPath(p) + '>')),
  );

  if (parents.length === 0) {
    return { parent: null, children: [] };
  }

  // Pick the deepest parent (most specific)
  const parent = parents.sort((a, b) => getPath(b).length - getPath(a).length)[0];

  // All descendants: starts with parent path
  const children = validElements.filter((el) => el !== parent && getPath(el).startsWith(getPath(parent) + '>'));

  return { parent, children };
};

const createField = (field, action, parentLayout = null) => {
  const locked = parentLayout?.attributes?.['data-ant-locked'] === 'true';
  const required = !!field?.attributes?.['required'];
  let visible = !field?.attributes?.['hidden'];

  if (parentLayout) {
    const { parent, children } = getParentChildrenPairsFromLayout(parentLayout.children);
    const visibilityFalseFromParent =
      parent?.attributes?.['hidden'] && children?.find((item) => item.cssPath === field.cssPath);
    if (visibilityFalseFromParent) {
      visible = false;
    }
  }

  let externalImageId = null;

  if (field?.element?.isImage) {
    const externalId = extractImageExternalId(field?.attributes?.src);
    if (externalId) {
      externalImageId = externalId;
    } else {
      const userImageId = extractUserImageFolderName(field?.attributes?.src);
      if (userImageId) {
        externalImageId = userImageId;
      }
    }
  }

  const elementValue = externalImageId && field?.element?.isImage ? externalImageId : field.element.text;

  return {
    visible,
    required,
    id: field.cssPath,
    value: encodeToBase64(elementValue),
    fieldName: field.attributes?.fieldName,
    objectType: field.element.tag,
    updated: action === EDITOR_ACTIONS.selectElement || action === EDITOR_ACTIONS.selectLayout ? true : !locked,
  };
};

const mapLayoutChildrenElements = (layout, action, selectedElement = null) => {
  if (selectedElement?.children?.length) {
    return selectedElement.children?.map((child) => createField(child, action, layout));
  }
  return selectedElement
    ? [createField(selectedElement, action)]
    : layout.children?.map((child) => createField(child, action, layout));
};

export const createLayout = (selectedLayout, action, selectedField) => ({
  id: selectedLayout.cssPath,
  fieldName: selectedLayout.attributes?.fieldName,
  layoutName: selectedLayout.attributes?.['layout-name'],
  locked: selectedLayout.attributes?.['data-ant-locked'] === 'true', // field for locked layout
  fields: mapLayoutChildrenElements(selectedLayout, action, selectedField),
});

export const isImage = ({ element, fieldFromLLM }) =>
  element?.tag?.includes('image') ||
  fieldFromLLM?.field_name?.toLowerCase().includes('image') ||
  fieldFromLLM?.field_name?.toLowerCase().includes('artwork');

export const isImageUrl = (url) => url.includes('veeva/') || url.includes('s3/') || url.includes('/images/users/');
// export const isImageUrl = (url) => url.includes('veeva/') || url.includes('s3/') || url.includes('/images/');

/**
 * Check if the URL is a cropped image URL (contains cropped- in the filename)
 * Handles URLs with query parameters (e.g., pre-signed S3 URLs)
 */
export const isCroppedImageUrl = (url) => {
  if (!url) return false;
  // Remove query parameters before checking
  const urlWithoutParams = url.split('?')[0];
  const fileName = urlWithoutParams.split('/').pop() || '';
  return fileName.startsWith('cropped-') && fileName.endsWith('.png');
};

/**
 * Extract the cropped filename from a URL (e.g., cropped-abc123.png)
 * Handles URLs with query parameters (e.g., pre-signed S3 URLs)
 */
export const extractCroppedFileName = (url) => {
  if (!url) return null;
  // Remove query parameters before extracting
  const urlWithoutParams = url.split('?')[0];
  const fileName = urlWithoutParams.split('/').pop() || '';
  if (fileName.startsWith('cropped-') && fileName.endsWith('.png')) {
    return fileName;
  }
  return null;
};

export const isEditableField = (fieldType) => editableFields.includes(fieldType);

export const extractImageExternalId = (url) => {
  if (!url) return null;
  // strip query params
  const urlWithoutParams = url.split('?')[0];
  // Capture the folder segment after images/ for veeva and s3 (preserve percent-encoding)
  const regex = /images\/(veeva\/\d+\/[^\/?#]+)|images\/(s3\/[^\/?#]+)/;
  const match = urlWithoutParams.match(regex);
  // match[1] for veeva, match[2] for s3
  const externalId = match ? match[1] || match[2] : null;
  // Return decoded externalId (convert %28/%29 to parentheses).
  if (!externalId) return null;
  try {
    return decodeURIComponent(externalId);
  } catch (e) {
    return externalId;
  }
};

export const extractUserImageFolderName = (url) => {
  if (!url) return null;
  // Support both URL patterns:
  // - /images/users/{userId}/{imageName}/{original-imageName}.{ext}
  // - <orgId>/email/<authDocId>/images/users/{imageName}/{original-imageName}.{ext}
  // Allow any prefix before /images/users/ and match common image extensions.
  // The regex handles an optional userId segment: images/users/(?:userId/)?folderName/filename.ext
  const regex = new RegExp(
    `(?:^|/)images/users/(?:([^/]+)/)?([^/]+)/[^/?#]+\.(png|jpg|jpeg|gif|webp|svg|bmp|tiff|avif)`,
    'i',
  );
  const match = url.match(regex);
  // folderName is in capture group 2 (whether userId was present or not)
  return match ? match[2] : null;
};

export const extractUserImageName = (userImageKey) => {
  // from key like user/${id} extract the {id} part
  let userImageId = null;
  const m = String(userImageKey || '').match(/users\/(?<id>[0-9a-fA-F\-]{36})/);
  if (m && m.groups && m.groups.id) {
    userImageId = m.groups.id;
  }
  return userImageId;
};

export const getDocumentKeyForUserImage = (url) => {
  const imageName = extractUserImageFolderName(url);
  if (imageName) {
    return `users/${imageName}`;
  }
  return null;
};

export const extractCroppedImageUrl = (imageSrc, imagesFromBe) => {
  const croppedFileName = extractCroppedFileName(imageSrc);
  const croppedVariant = imagesFromBe?.find((img) => img.variant === 'cropped' && img.name === croppedFileName);
  return croppedVariant?.url;
};

export const extractOriginalImageUrl = (id, imagesFromBe) => {
  const originalVariant = imagesFromBe?.find((img) => img.id === id && img.variant === 'original');
  return originalVariant?.url;
};

export const stripOldWesleyImports = (contentString) => {
  const scriptRgx = new RegExp(
    `<script\\b[^<]*src="[^"]*\/web-editor\/[^"]*"[^<]*?(?:(?!<\/script>)<[^<]*)*<\/scripts*>`,
    'gmi',
  );
  const styleRgx = new RegExp(`<link\\b[^>]*?href="[^\"]*\/web-editor\/[^"]*"[^>]*?>`, 'gmi');
  return contentString.replace(new RegExp(`${scriptRgx.source}|${styleRgx.source}`, 'gmi'), '');
};

export const mapEditorIdsToLLMIds = (fieldId, mapping) => {
  // Step 1: replace "body" with "mj-body" only at the start
  fieldId = fieldId.replace(/^body/, 'mj-body');

  // Step 2: replace IDs with mapping values if they exist
  return fieldId.replace(/#([A-Z0-9_]+)/g, (match, id) => {
    return '#' + (mapping[id] || id);
  });
};

export const mapTacticToTacticType = (tacticTypes) => {
  return tacticTypes.reduce((acc, type) => {
    if (type?.id) {
      return { ...acc, [type.id]: type.tacticContexts.map((item) => ({ ...item, regionDescriptionName: type.title })) };
    }
    return acc;
  }, {});
};
